import type { Patch } from "@freightsimple/shared/types";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import {
  errorMessageForDescription,
  errorMessageForTemperatureHandling,
} from "@/Shared/LineItems/Validators/lineItemsErrorMessages";
import {
  errorMessageForDimensionValue,
  errorMessageForHeightValue,
  isTooLarge,
} from "@/Shared/LineItems/Validators/lineItemsDimensionErrorMessages";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import type { ShipmentLimits } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentLimits";

export interface SavedCommodityErrors {
  handlingUnitType: ErrorMessageType;
  description: ErrorMessageType;
  skuIdentifier: ErrorMessageType;
  isStackable: ErrorMessageType;
  temperatureHandling: ErrorMessageType;
  dimensionsLength: ErrorMessageType;
  dimensionsWidth: ErrorMessageType;
  dimensionsHeight: ErrorMessageType;
  dangerousGoods: ErrorMessageType;
}

function errorMessageForSavedCommodityDescription(
  description: string | undefined,
  limits: ShipmentLimits,
): ErrorMessageType {
  const base = errorMessageForDescription(description, limits);
  if (base !== undefined) {
    return base;
  }
  const maxDescriptionLength = limits.savedCommodity.maxDescriptionLength;
  if (description !== undefined && description.length > maxDescriptionLength) {
    return `Must be ${maxDescriptionLength} characters or fewer`;
  }
  return undefined;
}

function errorMessageForSkuIdentifier(
  skuIdentifier: string | undefined,
  limits: ShipmentLimits,
): ErrorMessageType {
  const maxSkuLength = limits.savedCommodity.maxSkuIdentifierLength;
  if (skuIdentifier !== undefined && skuIdentifier.length > maxSkuLength) {
    return `Must be ${maxSkuLength} characters or fewer`;
  }
  return undefined;
}

function exceedsMax(value: string | undefined, max: number): boolean {
  return value !== undefined && value.length > max;
}

function errorMessageForDangerousGoods(
  savedCommodity: Patch<SavedCommodity>,
  limits: ShipmentLimits,
): ErrorMessageType {
  if (savedCommodity.isDangerous !== true) {
    return undefined;
  }

  const requiredMissing =
    savedCommodity.dangerousUnNumber === undefined ||
    savedCommodity.dangerousClassification === undefined ||
    savedCommodity.dangerousPackingGroup === undefined ||
    savedCommodity.dangerousProperShippingName === undefined;

  if (requiredMissing) {
    return "Missing dangerous goods details";
  }

  const dg = limits.dangerousGoods;
  const anyTooLong =
    exceedsMax(savedCommodity.dangerousUnNumber, dg.maxUnNumberLength) ||
    exceedsMax(
      savedCommodity.dangerousClassification,
      dg.maxClassificationLength,
    ) ||
    exceedsMax(
      savedCommodity.dangerousPackingGroup,
      dg.maxPackingGroupLength,
    ) ||
    exceedsMax(
      savedCommodity.dangerousProperShippingName,
      dg.maxProperShippingNameLength,
    ) ||
    exceedsMax(
      savedCommodity.dangerousTechnicalName,
      dg.maxTechnicalNameLength,
    ) ||
    exceedsMax(
      savedCommodity.dangerousPackagingType,
      dg.maxPackagingTypeLength,
    );

  if (anyTooLong) {
    return "Dangerous goods details exceed allowed length";
  }

  return undefined;
}

function hasAnyDimension(savedCommodity: Patch<SavedCommodity>): boolean {
  return [
    savedCommodity.lengthInches,
    savedCommodity.widthInches,
    savedCommodity.heightInches,
  ].some((d) => d !== undefined);
}

function errorMessageForDimension(
  value: number | undefined,
  validateValue: (value: number) => ErrorMessageType,
  anyDimensionFilled: boolean,
): ErrorMessageType {
  if (value === undefined) {
    return anyDimensionFilled ? "Required" : undefined;
  }
  return validateValue(value);
}

export function savedCommoditiesErrorMessages(
  savedCommodity: Patch<SavedCommodity>,
  limits: ShipmentLimits,
): SavedCommodityErrors {
  const anyDimensionFilled = hasAnyDimension(savedCommodity);
  const { lengthInches, widthInches, heightInches } = savedCommodity;

  const lengthError = errorMessageForDimension(
    lengthInches,
    (v) => errorMessageForDimensionValue(v, "long", limits),
    anyDimensionFilled,
  );
  const widthError = errorMessageForDimension(
    widthInches,
    (v) => errorMessageForDimensionValue(v, "wide", limits),
    anyDimensionFilled,
  );
  const heightError = errorMessageForDimension(
    heightInches,
    (v) => errorMessageForHeightValue(v, limits),
    anyDimensionFilled,
  );

  let finalLengthError = lengthError;
  let finalWidthError = widthError;
  if (
    finalLengthError === undefined &&
    finalWidthError === undefined &&
    lengthInches !== undefined &&
    lengthInches !== undefined &&
    widthInches !== undefined &&
    widthInches !== undefined &&
    isTooLarge(widthInches, lengthInches, limits)
  ) {
    finalLengthError = "Too large";
    finalWidthError = <span>{" "}</span>;
  }

  return {
    handlingUnitType:
      savedCommodity.handlingUnitType === undefined ? "Required" : undefined,
    description: errorMessageForSavedCommodityDescription(
      savedCommodity.description,
      limits,
    ),
    skuIdentifier: errorMessageForSkuIdentifier(
      savedCommodity.skuIdentifier,
      limits,
    ),
    isStackable:
      savedCommodity.isStackable === undefined ? "Required" : undefined,
    temperatureHandling:
      savedCommodity.temperatureHandling === undefined
        ? "Required"
        : errorMessageForTemperatureHandling(
            undefined,
            savedCommodity.temperatureHandling,
            limits,
          ),
    dimensionsLength: finalLengthError,
    dimensionsWidth: finalWidthError,
    dimensionsHeight: heightError,
    dangerousGoods: errorMessageForDangerousGoods(savedCommodity, limits),
  };
}
